/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {Uuid} from '../../../util/uuid';
import {AdnHttpService} from '../../../util/adn-http.service';
import {createAdnHttpResource, createAdnHttpResourceSender} from '../../../util/create-adn-http-resource';
import {LocalUserProfile} from '../../../session/local-user-profile';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';

function normalizeFloorPrice(au: any): any {
  au.floorPrice = au.floorPrice || {};
  return au;
}

/** Angular DI token for the AdUnit HTTP resource (factory product). */
export class AdUnitResource {
}

export function createAdUnitResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile
): any {
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const AdUnit = createAdnHttpResource(send, 'adunits', () => localUserProfile, 'adUnit');

  const baseGet = AdUnit.get;
  AdUnit.get = function(params: any, successCb?: any, errorCb?: any): any {
    const result = baseGet(params, undefined, undefined);
    result.$promise = result.$promise.then((au: any) => {
      const normalized = normalizeFloorPrice(au);
      (successCb || _.noop)(normalized);
      return normalized;
    }, (err: any) => {
      (errorCb || _.noop)(err);
      return Promise.reject(err);
    });
    return result;
  };

  const baseQuery = AdUnit.query;
  AdUnit.query = function(a1: any, a2: any, a3: any): any {
    let params: any;
    let success: any;
    let error: any;
    if (_.isFunction(a1)) {
      success = a1;
      error = a2;
    } else {
      params = a1;
      success = a2;
      error = a3;
    }

    const result = baseQuery(params);
    result.$promise = result.$promise.then((page: any) => {
      page.results = _.map(page.results, normalizeFloorPrice);
      (success || _.noop)(page);
      return page;
    }, (err: any) => {
      (error || _.noop)(err);
      return Promise.reject(err);
    });
    return result;
  };

  const baseSave = AdUnit.save;
  AdUnit.save = function(data: any): any {
    const amount = _.get(data.floorPrice, 'amount');
    if (!amount && amount !== 0) {
      data.floorPrice = null;
    }
    const result = baseSave(data);
    result.$promise = result.$promise.then(normalizeFloorPrice);
    return result;
  };

  AdUnit.create = function(defaults: any): any {
    return new AdUnit(_.merge({
      id: Uuid.generate(),
      urlAsCategory: true,
      pageSize: 1,
      targetingControl: 'NONE'
    }, defaults));
  };

  return AdUnit;
}

export const adUnitResourceProvider = {
  provide: AdUnitResource,
  useFactory: createAdUnitResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile]
};

